/**
 * UWM pricing math for Mana Pricing — one copy shared by the web page, the borrower
 * emails and the scheduler.
 *
 * Identical to the retired Pricing module's math (its page went with the module on
 * 2026-10-11; apps/api/src/services/pricing-offers.ts still carries it for My CRM):
 *   - UWM's final price is finalPriceAfterOriginationFee, as returned. It is the same for a
 *     lender-paid and a borrower-paid comp (verified on prod 2026-09-14: UWM deducts nothing), so a
 *     lender-paid comp is netted here: LENDER_PAID_COMP_PCT (1%) of the loan, like the rate sheets
 *   - lender-paid ("LLPA") temporary buydown = fixed factor × loan amount, in the price
 *   - borrower/seller-paid buydown = Σ years (note P&I − bought-down P&I) × 12, a cost for ★
 *   - CYP (Control Your Price) = a Mana-side basis-point credit on every final price
 *   - ★ = the LOWEST rate whose lender credit covers the closing costs + concession, and — for a
 *     borrower-paid comp only — the comp too (lender-paid: the credit is already net of the 1%
 *     comp, the same rule as the rate sheets; decided 2026-09-14); no fallback
 *   - APR shown to borrowers = rate + 0.6
 */
import { fieldValueList, parseBuydownOption, type BuydownChoice } from '../lender-pricing-fields.js';
import { LENDER_PAID_COMP_PCT } from '../pricing-comp.js';
import type { UwmLayoutField, UwmLoanFamily } from './bridge.js';

export interface UwmPriceDetail {
  label?: string;
  percent?: number;
  amount?: number;
}
export interface UwmPricePoint {
  interestRate?: { value?: number };
  adjustments?: UwmPriceDetail[];
  finalPrice?: { percent?: number; amount?: number } | null;
  finalPriceAfterOriginationFee?: { percent?: number; amount?: number } | null;
  principalAndInterest?: { value?: number };
  monthlyPayment?: { value?: number };
  mortgageInsurance?: UwmPriceDetail;
  annualPercentageRate?: number;
  isBestQuotePricePoint?: boolean;
}
export interface UwmValidQuote {
  mortgageProductName?: string;
  helocProductName?: string;
  mortgageProductAlias?: string;
  actualTermYears?: number;
  quotePricePoints?: UwmPricePoint[];
  /** Mana-side tags: the loan type this product was quoted under (set when the scenario priced several). */
  manaLoanFamily?: UwmLoanFamily;
  manaAmortization?: 'fixed' | 'arm';
}
export interface UwmInvalidQuote {
  mortgageProductName?: string;
  helocProductName?: string;
  failures?: Array<{ reasons?: string[] }>;
  invalidReasons?: string[];
  manaLoanFamily?: UwmLoanFamily;
  manaAmortization?: 'fixed' | 'arm';
}
export interface UwmQuoteResult {
  validQuoteItems?: UwmValidQuote[];
  invalidQuoteItems?: UwmInvalidQuote[];
  borrowerName?: string;
  legalDisclaimer?: string;
  obfuscatedScenarioId?: number;
  /**
   * Mana-side (priceUwm): per temporary-buydown choice (`uwmBuydownEligibilityKey`), the plain
   * product names UWM offers that buydown on — UWM's own answer to the quote re-sent with the
   * BuyDownAliasID (none on a refinance, none for jumbo or the FHA ARMs; checked 2026-11-02).
   * Buydown rows are built only for these products; a choice that is missing here (UWM could
   * not be asked) gets no rows.
   */
  manaBuydownEligible?: Record<string, string[]>;
}

/** Key of a temporary-buydown choice in `UwmQuoteResult.manaBuydownEligible`. */
export const uwmBuydownEligibilityKey = (choice: Pick<BuydownChoice, 'base' | 'paidBy'>): string => `${choice.base}|${choice.paidBy}`;
/** "Conforming Conventional 30 Year Fixed - Buydown 2-1 LLPA" (a product UWM returns for a buydown alias) → the plain product it was priced from. */
export const uwmPlainProductName = (name: string): string => name.replace(/\s*-\s*Buydown\b.*$/i, '').trim();

/** A temporary-buydown overlay on UWM's base products. isLlpa = lender-paid (priced as an LLPA). */
export type UwmBuydownSel = { base: string; label: string; isLlpa: boolean };

/** MLO comp + closing-cost thresholds for the ★ criteria (whole dollars). */
export interface UwmCriteria {
  comp: number;
  closing: number;
}
/** ★ criteria per loan type family, when a quote priced several (each family has its own comp category). */
export type UwmCriteriaByFamily = Partial<Record<UwmLoanFamily, UwmCriteria | null>>;

/** The ★ criteria for one UWM product: its loan type family's when the quote priced several, else the quote's. */
export function uwmCriteriaFor(q: { manaLoanFamily?: UwmLoanFamily }, criteria: UwmCriteria | null, byFamily?: UwmCriteriaByFamily | null): UwmCriteria | null {
  if (byFamily && q.manaLoanFamily && q.manaLoanFamily in byFamily) return byFamily[q.manaLoanFamily] ?? null;
  return criteria;
}

export const UWM_BUYDOWN_STEPS: Record<string, number[]> = {
  '1-0': [0.01],
  '1-1': [0.01, 0.01],
  '2-1': [0.02, 0.01],
  '3-2-1': [0.03, 0.02, 0.01],
};
export const UWM_BUYDOWN_LLPA_FACTOR: Record<string, number> = {
  '1-0': 0.00875,
  '1-1': 0.015,
  '2-1': 0.02375,
  '3-2-1': 0.045,
};

/** APR shown for every lender (decided 2026-09-13): note rate + 0.6. */
export const MANA_APR_SPREAD = 0.6;
export const manaApr = (ratePct: number): number => Math.round((ratePct + MANA_APR_SPREAD) * 1000) / 1000;

export const uwmProductName = (q: UwmValidQuote | UwmInvalidQuote): string => q.mortgageProductName ?? q.helocProductName ?? '—';

export function uwmPmt(annualRate: number, nMonths: number, principal: number): number {
  const r = annualRate / 12;
  if (nMonths <= 0) return 0;
  if (r <= 0) return principal / nMonths;
  return (principal * r) / (1 - Math.pow(1 + r, -nMonths));
}

/** Non-LLPA (seller/borrower-paid) buydown cost = Σ years (monthly savings × 12). */
export function uwmBuydownConcession(noteRatePct: number, termYears: number, loan: number, steps: number[]): number {
  const n = termYears * 12;
  const note = uwmPmt(noteRatePct / 100, n, loan);
  return steps.reduce((sum, step) => sum + (note - uwmPmt(noteRatePct / 100 - step, n, loan)) * 12, 0);
}

/** UWM's final price (percent = cost points, amount = dollars; negative = credit). */
export const uwmFinalPriceDetail = (p?: UwmPricePoint) => p?.finalPriceAfterOriginationFee ?? p?.finalPrice ?? undefined;

/** Lender-paid buydown cost as a synthetic adjustment (factor × loan amount). */
export function uwmTempBuydownAdj(buydown: UwmBuydownSel | null, loanAmount: number): UwmPriceDetail | null {
  if (!buydown || !buydown.isLlpa) return null;
  const factor = UWM_BUYDOWN_LLPA_FACTOR[buydown.base] ?? 0;
  if (!factor) return null;
  return { label: `Temporary Buydown ${buydown.base}`, percent: factor * 100, amount: factor * loanAmount };
}

/** Control-Your-Price incentive: +bps improves the price (credit to the borrower). */
export function uwmCypAdj(cypBps: number, loanAmount: number): UwmPriceDetail | null {
  if (!cypBps) return null;
  return { label: `CYP ${cypBps} bps`, percent: -(cypBps / 100), amount: -((cypBps * loanAmount) / 10000) };
}

/**
 * Lender-paid broker comp as a synthetic adjustment (a cost: it comes out of the lender credit).
 * lpCompPct null = borrower-paid comp, nothing netted.
 */
export function uwmLpCompAdj(lpCompPct: number | null | undefined, loanAmount: number): UwmPriceDetail | null {
  if (lpCompPct == null || !(lpCompPct > 0)) return null;
  return { label: `Lender-paid comp (${lpCompPct.toFixed(3)}%)`, percent: lpCompPct, amount: (lpCompPct / 100) * loanAmount };
}

/** The lender-paid comp percent a UWM request prices with (null = borrower-paid). */
export function uwmLpCompPctFor(body: Record<string, unknown> | null | undefined, layout?: UwmLayoutField[] | null): number | null {
  return uwmIsLenderPaidComp(body, layout) ? LENDER_PAID_COMP_PCT : null;
}

/** Final price including the lender-paid buydown cost, any CYP incentive and a lender-paid comp. */
export function uwmEffectiveFinal(
  point: UwmPricePoint,
  buydown: UwmBuydownSel | null,
  loanAmount: number,
  cypBps = 0,
  lpCompPct: number | null = null,
): { percent?: number; amount?: number } | undefined {
  const base = uwmFinalPriceDetail(point);
  const tb = uwmTempBuydownAdj(buydown, loanAmount);
  const cyp = uwmCypAdj(cypBps, loanAmount);
  const lp = uwmLpCompAdj(lpCompPct, loanAmount);
  if (!tb && !cyp && !lp) return base ?? undefined;
  return {
    percent: (base?.percent ?? 0) + (tb?.percent ?? 0) + (cyp?.percent ?? 0) + (lp?.percent ?? 0),
    amount: (base?.amount ?? 0) + (tb?.amount ?? 0) + (cyp?.amount ?? 0) + (lp?.amount ?? 0),
  };
}

/** Monthly payment = P&I + monthly mortgage insurance. */
export function uwmMonthlyPayment(p?: UwmPricePoint): number | undefined {
  const pi = p?.principalAndInterest?.value;
  if (typeof pi !== 'number') return undefined;
  return pi + (p?.mortgageInsurance?.amount ?? 0);
}

export interface UwmBestCtx {
  buydown: UwmBuydownSel | null;
  loanAmount: number;
  termYears: number;
  cypBps: number;
  criteria: UwmCriteria | null;
  /** Lender-paid comp percent netted out of the credit (null/undefined = borrower-paid comp). */
  lpCompPct?: number | null;
}

/** Whether a UWM request prices the broker comp as lender-paid (Compensation Payer Type "Lender paid", id 1). */
export function uwmIsLenderPaidComp(body: Record<string, unknown> | null | undefined, layout?: UwmLayoutField[] | null): boolean {
  const raw = body?.compensationPayerTypeID;
  const id = String(Array.isArray(raw) ? (raw[0] ?? '') : (raw ?? '')).trim();
  if (!id) return false;
  const label = layout?.find((f) => f.itemId === 'CompensationPayerTypeID')?.selectListData?.find((o) => o.id === id)?.displayValue;
  return label ? /lender/i.test(label) : id === '1';
}

/** Lender credit (dollars, + = credit) at a point, after the buydown LLPA, CYP and a lender-paid comp. */
export function uwmLenderCredit(p: UwmPricePoint, buydown: UwmBuydownSel | null, loanAmount: number, cypBps: number, lpCompPct: number | null = null): number | null {
  const rate = p.interestRate?.value;
  if (typeof rate !== 'number') return null;
  return -(uwmEffectiveFinal(p, buydown, loanAmount, cypBps, lpCompPct)?.amount ?? 0);
}

/**
 * Index of the LOWEST-rate point whose net lender credit meets the comp criteria:
 *   netCredit = lenderCredit − (closingCosts + non-LLPA buydown cost)
 *   borrower-paid comp: netCredit ≥ comp
 *   lender-paid comp:   netCredit ≥ 0, with lenderCredit already net of the 1% comp
 * −1 when nothing qualifies or there are no criteria (no par fallback).
 */
export function uwmCriteriaPointIndex(points: UwmPricePoint[], ctx: UwmBestCtx): number {
  if (points.length === 0 || !ctx.criteria) return -1;
  const { closing } = ctx.criteria;
  const lpCompPct = ctx.lpCompPct ?? null;
  const comp = lpCompPct != null ? 0 : ctx.criteria.comp;
  const steps = ctx.buydown && !ctx.buydown.isLlpa ? UWM_BUYDOWN_STEPS[ctx.buydown.base] : undefined;
  let idx = -1;
  let bestRate = Infinity;
  points.forEach((p, i) => {
    const c = uwmLenderCredit(p, ctx.buydown, ctx.loanAmount, ctx.cypBps, lpCompPct);
    const rate = p.interestRate?.value;
    if (c == null || typeof rate !== 'number') return;
    let costs = closing;
    if (steps && ctx.termYears > 0 && ctx.loanAmount > 0) costs += uwmBuydownConcession(rate, ctx.termYears, ctx.loanAmount, steps);
    if (c - costs >= comp && rate < bestRate) {
      bestRate = rate;
      idx = i;
    }
  });
  return idx;
}

// ---------------------------------------------------------------------------
// Email programs (the Term × Program grid) ↔ temporary-buydown options
// ---------------------------------------------------------------------------

export interface UwmProgram {
  key: string;
  label: string;
  emailLabel: string;
  buydown: UwmBuydownSel | null;
  /** The matching Temporary Buydown option (lender-pricing-fields BUYDOWN_OPTIONS), 'None' for fixed. */
  buydownOption: string;
}

/**
 * The email's program columns — one per Temporary Buydown option of the scenario (Fixed = "None"),
 * in this column order. The first six are the Pricing module's programs; the other three cover the
 * remaining buydown options so every scenario selection has a column.
 */
export const UWM_PROGRAMS: UwmProgram[] = [
  { key: 'fixed', label: 'Fixed', emailLabel: 'Fixed Rate', buydown: null, buydownOption: 'None' },
  { key: '1-0bp', label: '1-0 BP', emailLabel: '1-0 Buydown', buydown: { base: '1-0', label: '1-0', isLlpa: false }, buydownOption: '1/0 Seller/Borrower-paid' },
  { key: '1-1bp', label: '1-1 BP', emailLabel: '1-1 Buydown', buydown: { base: '1-1', label: '1-1', isLlpa: false }, buydownOption: '1/1 Seller/Borrower-paid' },
  { key: '2-1bp', label: '2-1 BP', emailLabel: '2-1 Buydown', buydown: { base: '2-1', label: '2-1', isLlpa: false }, buydownOption: '2/1 Seller/Borrower-paid' },
  { key: '3-2-1bp', label: '3-2-1 BP', emailLabel: '3-2-1 Buydown', buydown: { base: '3-2-1', label: '3-2-1', isLlpa: false }, buydownOption: '3/2/1 Seller/Borrower-paid' },
  { key: '1-0llpa', label: '1-0 LLPA', emailLabel: '1-0 Buydown LLPA', buydown: { base: '1-0', label: '1-0 LLPA', isLlpa: true }, buydownOption: '1/0 Lender-paid' },
  { key: '1-1llpa', label: '1-1 LLPA', emailLabel: '1-1 Buydown LLPA', buydown: { base: '1-1', label: '1-1 LLPA', isLlpa: true }, buydownOption: '1/1 Lender-paid' },
  { key: '2-1llpa', label: '2-1 LLPA', emailLabel: '2-1 Buydown LLPA', buydown: { base: '2-1', label: '2-1 LLPA', isLlpa: true }, buydownOption: '2/1 Lender-paid' },
  { key: '3-2-1llpa', label: '3-2-1 LLPA', emailLabel: '3-2-1 Buydown LLPA', buydown: { base: '3-2-1', label: '3-2-1 LLPA', isLlpa: true }, buydownOption: '3/2/1 Lender-paid' },
];

/**
 * The email's program columns for a scenario's Temporary Buydown selection (the field's raw
 * value: a list of options, a single option, or blank): "None" → Fixed, "2/1 Lender-paid" →
 * 2-1 LLPA, … in UWM_PROGRAMS order. Blank / unknown → Fixed only, so an email always has a column.
 */
export function programKeysForBuydowns(raw: unknown): string[] {
  const keys = new Set<string>();
  for (const v of fieldValueList(raw)) {
    const choice = /^none$/i.test(v) ? null : parseBuydownOption(v);
    if (choice === null && !/^none$/i.test(v)) continue; // not a buydown option
    const p = uwmProgramForChoice(choice);
    if (p) keys.add(p.key);
  }
  const ordered = UWM_PROGRAMS.filter((p) => keys.has(p.key)).map((p) => p.key);
  return ordered.length ? ordered : ['fixed'];
}

/** The Temporary Buydown options that give these program columns (the inverse of programKeysForBuydowns). */
export function buydownOptionsForPrograms(keys: string[]): string[] {
  const options = UWM_PROGRAMS.filter((p) => keys.includes(p.key)).map((p) => p.buydownOption);
  return options.length ? options : ['None'];
}

/**
 * Program columns for a grid email: the scenario's Temporary Buydown selection when the scenario
 * carries that field, else the stored program keys (schedules saved before the field drove it).
 */
export function gridProgramKeys(inputs: Record<string, unknown> | null | undefined, storedKeys: string[] | null | undefined): string[] {
  const raw = inputs?.buydown;
  if (raw !== undefined && raw !== null && fieldValueList(raw).length > 0) return programKeysForBuydowns(raw);
  const stored = UWM_PROGRAMS.filter((p) => (storedKeys ?? []).includes(p.key)).map((p) => p.key);
  return stored.length ? stored : ['fixed'];
}

/** UWM overlay for a Temporary Buydown choice (every option has UWM math: factor or steps). */
export function uwmBuydownForChoice(choice: BuydownChoice | null): UwmBuydownSel | null {
  if (!choice) return null;
  const isLlpa = choice.paidBy === 'lender';
  return { base: choice.base, label: `${choice.base}${isLlpa ? ' LLPA' : ''}`, isLlpa };
}

/** The email program for a Temporary Buydown choice, or null when the grid email has no such column. */
export function uwmProgramForChoice(choice: BuydownChoice | null): UwmProgram | null {
  if (!choice) return UWM_PROGRAMS[0]!;
  return UWM_PROGRAMS.find((p) => p.buydown && p.buydown.base === choice.base && p.buydown.isLlpa === (choice.paidBy === 'lender')) ?? null;
}
